import { useEffect, useMemo, useState, useRef } from 'react';
import { ChevronDown, CheckCircle, CornerLeftUp, FileText, FolderPlus, Video } from "react-feather";
import GroupKebabPopover from './GroupKebabPopover';
import PortfolioGroup from './PortfolioGroup';
import useWindowDimensions, { formatDate, useRemote, useStorage } from '../../../Utils/Utils';
import { useCustomGroups } from '../../../Utils/useCustomGroups';
import { usePortfolioPools } from '../../../Utils/usePortfolioPools';
import { useSelectionSync } from '../../../Utils/useSelectionSync';
import { resolveGroupMemberMode as resolveGroupMemberModeImpl, groupModeOf, collectEditableDataNames, resolveVideoFieldNames, findIncompleteGroupMembers } from '../../../Utils/customGroupsHelpers';
import Select, { components } from 'react-select'
import Button from '../../Buttons/Button';
import AlertBlock from '../../../layouts/Shared/Prompts/AlertBlock';
import Tooltip from '../../popups/Tooltip';
import Modal from '../../Modals/Modal';
import PortfolioGroupsModal from './PortfolioGroupsModal';
import './ExternalManagerPortfoliosSelector.scss'


const DropdownIndicator = (props) => (
    <components.DropdownIndicator {...props}>
        <ChevronDown
            size={18}
            className="dropdown-chevron"
            style={{
                transition: "transform 0.2s",
                transform: props.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)"
            }}
        />
    </components.DropdownIndicator>
);

const ExternalManagerPortfoliosSelector = ({ updateTickerStatus, portfolios, products, setState, state, template, setPortfoliosState, setPortfoliosGroupedState, setPortfolioMode, exposeMoveToReady, exposeSetMode, onGroupsChange, showVideoToggle }) => {
    const { height } = useWindowDimensions()
    const [tags, setTags] = useState([])
    const [pendingExpanded, setPendingExpanded] = useState(true)
    const [readyExpanded, setReadyExpanded] = useState(false)
    const [selectedFund, setSelectedFund] = useState(null)
    const [showGroupsModal, setShowGroupsModal] = useState(false);
    // Separate dicts so opening Ungrouped Pending/Ready doesn't collapse a
    // selected group (the previous shared-dict accordion did).
    const [expandedGroups, setExpandedGroups] = useState({});
    const [expandedUngrouped, setExpandedUngrouped] = useState({ pending: true, ready: true });
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [openKebab, setOpenKebab] = useState(null);
    const [kebabPos, setKebabPos] = useState(null);
    const [recentlyMovedGroup, setRecentlyMovedGroup] = useState(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);
    const [confirmModeChange, setConfirmModeChange] = useState(null); // { groupId, newLinked }
    const [renamingGroupId, setRenamingGroupId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    // { group, incomplete: [{ fundNumber, fundName, reason }] } when the
    // move-to-ready empty-content pre-check finds incomplete members.
    const [readyPrecheck, setReadyPrecheck] = useState(null);
    const precheckingRef = useRef(false);


    const selectedGroupStatusRef = useRef(null);
    const skipFundEffectRef = useRef(false);
    const appliedUrlTickerRef = useRef(null);
    // Tracks the last intent pushed to BuilderPage's setPortfolioMode for the
    // group-context case: true => member-preview ("Previewing individual portfolio"),
    // false => group view ("Editing N portfolios"). Used by the videoModes
    // reconciliation effect so it preserves whichever banner was most recently
    // requested rather than hard-coding one or the other.
    const isGroupMemberPreviewRef = useRef(true);

    const completedKey = 'manager_commentary_complete'; //hardcoded static data key.

    const remote = useRemote();
    const storage = useStorage();

    const {
        pending, ready, videoModes, setVideoModes,
        pendingRef, readyRef,
        completed, completedDetails,
        setContentType, moveToReady, moveToPending,
        moveGroupToReady: moveGroupToReadyImpl, moveGroupToPending: moveGroupToPendingImpl,
        markAsComplete,
    } = usePortfolioPools({
        portfolios, template, remote, storage,
        completedKey,
        selectedFund, selectedGroup, setPortfolioMode,
        setRecentlyMovedGroup,
        setPortfoliosState,
        setReadyExpanded, setPendingExpanded,
    });

    const {
        groups, groupsLoaded,
        idLookup,
        saveGroups, deleteGroupById, toggleGroupLinked, commitRename: commitRenameImpl,
    } = useCustomGroups({
        remote, portfolios, template,
        onGroupsChange,
        pendingRef, readyRef,
        videoModes, setVideoModes,
        selectedGroup, selectedGroupStatusRef, isGroupMemberPreviewRef,
        setSelectedFund, setSelectedGroup,
        skipFundEffectRef,
        setPortfolioMode,
    });

    function commitRename(id) {
        const trimmed = renameValue.trim();
        if (!trimmed) { setRenamingGroupId(null); setRenameValue(''); return; }
        commitRenameImpl(id, trimmed, () => {
            setRenamingGroupId(null);
            setRenameValue('');
        });
    }

    // Sort a copy — `Array.prototype.sort` mutates in place and `portfolios`
    // arrives via prop. Memoise so react-select doesn't get a new array on
    // every render.
    const sortedPortfolioOptions = useMemo(() => {
        if (!portfolios || portfolios.length === 0) return [];
        return [...portfolios]
            .sort((a, b) => {
                if (!a.fundName) return -1;
                if (!b.fundName) return 1;
                return a.fundName.localeCompare(b.fundName);
            })
            .map(f => ({ value: f, label: f.fundName }));
    }, [portfolios]);

    const moveToReadyRef = useRef();

    // Keep the ref pointing at the latest moveSingleToReady closure on every
    // render, so the parent-triggered move gets the same empty-content
    // pre-check as the sidebar row button.
    // Done in an effect (not at render time) because function declarations
    // inside the component are hoisted as `undefined` in strict-mode CRA/Babel
    // output until the function body initialises them. The exposed callback
    // below reads moveToReadyRef.current at call time, so this is safe.
    useEffect(() => {
        moveToReadyRef.current = moveSingleToReady;
    });

    useEffect(() => {
        if (exposeMoveToReady) exposeMoveToReady((portfolio) => moveToReadyRef.current && moveToReadyRef.current(portfolio));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exposeMoveToReady]);

    useEffect(() => {
        if (!exposeSetMode) return;
        if (selectedGroup) {
            exposeSetMode((type) => {
                const pool = selectedGroupStatusRef.current === 'ready' ? readyRef.current : pendingRef.current;
                const members = pool.filter(p => selectedGroup.fundNumbers.includes(p.fundNumber));
                const primary = members[0];
                if (!primary) return;
                // Linked invariant: write the new mode to all members and PUT contentType
                // for each so the next reload reads back the canonical state.
                members.forEach(p => {
                    remote.put(`savev2/${btoa(p.fundNumber)}/${template}`, { contentType: type });
                });
                setVideoModes(prev => {
                    const next = { ...prev };
                    for (const p of members) next[p.fundNumber] = type;
                    return next;
                });
                setSelectedFund(primary);
                if (setPortfolioMode) {
                    setPortfolioMode({
                        mode: type,
                        fundNumber: primary.fundNumber,
                        name: selectedGroup.name,
                        members,
                        status: selectedGroupStatusRef.current || 'pending',
                        isMemberPreview: false,
                        linked: selectedGroup.linked !== false,
                    });
                }
            });
        } else if (selectedFund) {
            exposeSetMode((type) => handleSetContentType(selectedFund, type));
        } else {
            exposeSetMode(null);
        }
    }, [selectedFund, selectedGroup]);

    useEffect(() => {
        if (portfolios && portfolios.length > 0) {
            const availableProducts = [...new Set(portfolios.map(p => p.offerName))]
            if (products && products.length > 0) {
                setTags(products.map(p => {
                    return { ...p, selected: availableProducts.includes(p.offerName), disabled: !availableProducts.includes(p.offerName) }
                }))
            }
        }
    }, [portfolios, products])

    useSelectionSync({
        selectedFund, selectedGroup, videoModes,
        pendingRef, readyRef,
        selectedGroupStatusRef, isGroupMemberPreviewRef, skipFundEffectRef,
        setPortfolioMode, setState,
    });

    // Bridge the URL/parent-supplied ticker (state.tickers[0]) into the
    // selector's internal selectedFund AND, if the ticker belongs to a
    // custom group, selectedGroup. Applies once per URL ticker change via
    // appliedUrlTickerRef so that subsequent pool/group mutations can't
    // clobber a user's later click selection. Without this, deep-link
    // URLs like /editor?fund=85447 leave BuilderPage with no group
    // context — so Mark-as-Ready in the VideoUploadPanel keeps showing
    // even when the fund is in a group, and the mode pushed to
    // BuilderPage stays on the default 'editor'.
    useEffect(() => {
        const ticker = state?.tickers?.[0];
        if (!ticker) return;
        if (appliedUrlTickerRef.current === ticker) return;
        if (!groupsLoaded) return;  // wait for groups so owningGroup is evaluated correctly
        const match = pendingRef.current.find(p => p.fundNumber === ticker)
            || readyRef.current.find(p => p.fundNumber === ticker);
        if (!match) return;  // pools not loaded yet — retry on next dep change
        const owningGroup = groups.find(g => g.fundNumbers.includes(ticker)) || null;
        appliedUrlTickerRef.current = ticker;
        if (owningGroup) {
            const status = readyRef.current.some(p => p.fundNumber === ticker) ? 'ready' : 'pending';
            selectedGroupStatusRef.current = status;
            // Deep-link to a fund inside a group = "viewing that fund" ≈ member preview.
            isGroupMemberPreviewRef.current = true;
        }
        setSelectedGroup(owningGroup);
        setSelectedFund(match);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.tickers, pending, ready, groups, groupsLoaded]);

    // Wrap setContentType from the hook to also focus the portfolio in the selector.
    function handleSetContentType(portfolio, type, e) {
        setSelectedFund(portfolio);
        setContentType(portfolio, type, e);
    }

    function updateTags(index) {
        const newArr = tags.map((t, idx) => {
            if (idx === index) {
                return { ...t, selected: !t.selected }
            } else return t
        })
        const selectedOffers = newArr.filter(t => t.selected).map(t => t.offerName)
        setPortfoliosGroupedState(selectedOffers)
        setTags(newArr)
    }

    function chooseFund(fund) {
        // INTENTIONAL (feature, not a bug): if the chosen portfolio belongs to a
        // custom group, we load the whole group (identical to clicking the group
        // header) rather than the lone portfolio. Selecting any member of a group
        // must behave the same as selecting the group itself — grouped portfolios
        // are edited together, so loading one in isolation would be misleading.
        // Every other selection path already resolves the owning group; the Search
        // dropdown is just brought in line here. If a future requirement wants the
        // dropdown to open a grouped portfolio individually, change this block
        // deliberately — it is not an oversight.
        // setState/setPortfolioMode are driven by useSelectionSync off selectedFund,
        // so we don't push them directly here.
        const owningGroup = groups.find(g => g.fundNumbers.includes(fund.fundNumber)) || null;
        if (owningGroup) {
            const status = readyRef.current.some(p => p.fundNumber === fund.fundNumber) ? 'ready' : 'pending';
            selectGroup(owningGroup, status);
            // Mirror header-click UX: force-expand the group in the list.
            setExpandedGroups({ [owningGroup.id]: true });
        } else {
            setSelectedGroup(null);
            setSelectedFund(fund);
        }
    };

    function tagIsSelected(portfolio) {
        const tagsObj = tags.reduce((a, c) => ({ ...a, [c.offerName]: c }), {})
        const theTag = tagsObj[portfolio.offerName]
        return theTag?.selected
    }

    async function handleSaveGroups(newGroups) {
        const result = await saveGroups(newGroups);
        if (!result || !result.ok) return;
        setShowGroupsModal(false);
        // Re-resolve the current selection against the just-saved membership.
        // Without this, a portfolio that was selected individually and then
        // grouped keeps its lone-fund context — BuilderPage never gets the
        // group's members, so edits save single-ticker instead of emitting
        // to the whole group.
        const fresh = result.groups || [];
        const owning = selectedFund
            ? fresh.find(g => g.fundNumbers.includes(selectedFund.fundNumber)) || null
            : null;
        if (owning && (!selectedGroup || selectedGroup.id !== owning.id)) {
            // Current portfolio just became (or moved) a group member — load
            // the group, same UX as chooseFund's owning-group rule.
            const status = readyRef.current.some(p => p.fundNumber === selectedFund.fundNumber) ? 'ready' : 'pending';
            selectGroup(owning, status);
            setExpandedGroups({ [owning.id]: true });
        } else if (owning) {
            // Same group, membership/mode may have changed — swap in the fresh
            // object; useSelectionSync re-pushes members, preserving preview.
            setSelectedGroup(owning);
        } else if (selectedGroup) {
            // Fund removed from its group (or group deleted) — back to
            // lone-fund mode; useSelectionSync re-pushes without members.
            setSelectedGroup(null);
        }
    }

    function toggleGroupExpand(id) {
        // Accordion within real groups (numeric ids) — opening one collapses
        // the others. Does NOT touch the ungrouped buckets, which use their
        // own state.
        setExpandedGroups(prev => prev[id] === true ? {} : { [id]: true });
    }

    function isGroupExpanded(id) {
        return expandedGroups[id] === true;
    }

    function toggleUngroupedExpand(bucket) { // 'pending' | 'ready'
        setExpandedUngrouped(prev => ({ ...prev, [bucket]: !prev[bucket] }));
    }

    function isUngroupedExpanded(bucket) {
        return expandedUngrouped[bucket] === true;
    }

    function selectGroup(group, status) {
        skipFundEffectRef.current = true;
        const pool = status === 'ready' ? readyRef.current : pendingRef.current;
        const members = pool.filter(p => group.fundNumbers.includes(p.fundNumber));
        const primary = members[0];
        selectedGroupStatusRef.current = status || 'pending';
        isGroupMemberPreviewRef.current = false;
        setSelectedGroup(group);
        setSelectedFund(primary || null);
        if (setPortfolioMode && primary) {
            const currentMode = resolveGroupMemberMode(group, primary);
            setPortfolioMode({
                mode: currentMode,
                fundNumber: primary.fundNumber,
                name: group.name,
                members,
                status: status || 'pending',
                isMemberPreview: false,
                linked: group.linked !== false,
            });
        }
    }

    // Pre-check before moving a group to Ready: re-fetch each member's content
    // and warn (via modal) if any is empty for its contentType. Confirm in the
    // modal proceeds; cancel does nothing. Fails open on fetch error so a
    // transient network issue can't block publishing.
    async function moveGroupToReady(group) {
        if (precheckingRef.current) return;
        const members = pendingRef.current.filter(p => group.fundNumbers.includes(p.fundNumber));
        if (members.length === 0) return;
        precheckingRef.current = true;
        try {
            const tdoc = await remote.get('/data/templates/' + template);
            const editableDataNames = collectEditableDataNames(tdoc);
            const videoFieldNames = resolveVideoFieldNames(tdoc);
            const blobs = await Promise.all(
                members.map(m => remote.get(`retrievev2/${btoa(m.fundNumber)}/${template}`))
            );
            const contentByFund = {};
            members.forEach((m, i) => { contentByFund[m.fundNumber] = blobs[i] || {}; });
            const incomplete = findIncompleteGroupMembers({ members, contentByFund, editableDataNames, videoFieldNames });
            if (incomplete.length > 0) {
                setReadyPrecheck({ group, incomplete });
                return;
            }
            doMoveGroupToReady(group);
        } catch (e) {
            console.error('move-to-ready precheck failed; proceeding', e);
            doMoveGroupToReady(group);
        } finally {
            precheckingRef.current = false;
        }
    }

    // Single-portfolio counterpart of moveGroupToReady: same empty-content
    // pre-check before an ungrouped portfolio lands in Ready to Publish.
    // Mode (video vs editor) comes from the freshly fetched blob's
    // contentType, not videoModes state, which can be stale vs the server.
    async function moveSingleToReady(portfolio) {
        if (precheckingRef.current) return;
        precheckingRef.current = true;
        try {
            const tdoc = await remote.get('/data/templates/' + template);
            const editableDataNames = collectEditableDataNames(tdoc);
            const videoFieldNames = resolveVideoFieldNames(tdoc);
            const blob = await remote.get(`retrievev2/${btoa(portfolio.fundNumber)}/${template}`);
            const incomplete = findIncompleteGroupMembers({
                members: [portfolio],
                contentByFund: { [portfolio.fundNumber]: blob || {} },
                editableDataNames,
                videoFieldNames,
            });
            if (incomplete.length > 0) {
                setReadyPrecheck({ portfolio, incomplete });
                return;
            }
            moveToReady(portfolio);
        } catch (e) {
            console.error('move-to-ready precheck failed; proceeding', e);
            moveToReady(portfolio);
        } finally {
            precheckingRef.current = false;
        }
    }

    function doMoveGroupToReady(group) {
        moveGroupToReadyImpl(group, ({ newPool }) => {
            if (selectedGroup?.id !== group.id || !setPortfolioMode) return;
            selectedGroupStatusRef.current = 'ready';
            isGroupMemberPreviewRef.current = false;
            const newMembers = newPool.filter(p => group.fundNumbers.includes(p.fundNumber));
            const primary = newMembers[0];
            if (!primary) return;
            setPortfolioMode({
                mode: resolveGroupMemberMode(group, primary),
                fundNumber: primary.fundNumber,
                name: group.name,
                members: newMembers,
                status: 'ready',
                isMemberPreview: false,
                linked: group.linked !== false,
            });
        });
    }

    function moveGroupToPending(group) {
        moveGroupToPendingImpl(group, ({ newPool }) => {
            if (selectedGroup?.id !== group.id || !setPortfolioMode) return;
            selectedGroupStatusRef.current = 'pending';
            isGroupMemberPreviewRef.current = false;
            const newMembers = newPool.filter(p => group.fundNumbers.includes(p.fundNumber));
            const primary = newMembers[0];
            if (!primary) return;
            setPortfolioMode({
                mode: resolveGroupMemberMode(group, primary),
                fundNumber: primary.fundNumber,
                name: group.name,
                members: newMembers,
                status: 'pending',
                isMemberPreview: false,
                linked: group.linked !== false,
            });
        });
    }

    // Binds the pure helper to the closure's videoModes.
    function resolveGroupMemberMode(group, member) {
        return resolveGroupMemberModeImpl(group, member, videoModes);
    }

    function togglePortfolioMode(p, group, e) {
        e.stopPropagation();
        const currentMode = videoModes[p.fundNumber] || 'editor';
        const newMode = currentMode === 'video' ? 'editor' : 'video';
        const encodedPortfolio = btoa(p.fundNumber);
        remote.put(`savev2/${encodedPortfolio}/${template}`, { contentType: newMode });
        setVideoModes(prev => ({ ...prev, [p.fundNumber]: newMode }));
        if (setPortfolioMode) {
            setSelectedFund(p);
            setSelectedGroup(group);
            if (group) {
                const pool = selectedGroupStatusRef.current === 'ready' ? readyRef.current : pendingRef.current;
                const members = pool.filter(m => group.fundNumbers.includes(m.fundNumber));
                isGroupMemberPreviewRef.current = true;
                setPortfolioMode({
                    mode: newMode,
                    fundNumber: p.fundNumber,
                    name: group.name,
                    members,
                    status: selectedGroupStatusRef.current || 'pending',
                    isMemberPreview: true,
                    linked: group.linked !== false,
                });
            } else {
                const status = readyRef.current.some(r => r.fundNumber === p.fundNumber) ? 'ready' : 'pending';
                setPortfolioMode({
                    mode: newMode,
                    fundNumber: p.fundNumber,
                    name: p.fundName,
                    status,
                });
            }
        }
    }

    function toggleGroupMode(group, members, status, e) {
        e.stopPropagation();
        const currentMode = groupModeOf(group, members, videoModes);
        const newMode = currentMode === 'video' ? 'editor' : 'video';
        members.forEach(p => {
            const encodedPortfolio = btoa(p.fundNumber);
            remote.put(`savev2/${encodedPortfolio}/${template}`, { contentType: newMode });
        });
        setVideoModes(prev => {
            const next = { ...prev };
            for (const p of members) next[p.fundNumber] = newMode;
            return next;
        });
        if (selectedGroup?.id === group.id && setPortfolioMode && members[0]) {
            isGroupMemberPreviewRef.current = false;
            setPortfolioMode({
                mode: newMode,
                fundNumber: members[0].fundNumber,
                name: group.name,
                members,
                status,
                isMemberPreview: false,
                linked: true,
            });
        }
    }

    const groupedFundNumbers = new Set(groups.flatMap(g => g.fundNumbers));
    const ungroupedPending = pending.filter(p => !groupedFundNumbers.has(p.fundNumber));
    const hasGroups = groups.length > 0;
    const pendingGroups = groups.filter(g => g.fundNumbers.some(fn => pending.some(p => p.fundNumber === fn)));
    const readyGroups = groups.filter(g =>
        g.fundNumbers.some(fn => ready.some(p => p.fundNumber === fn))
    );
    const ungroupedReady = ready.filter(p => !groupedFundNumbers.has(p.fundNumber));

    return (
        <>
        <div>
            {showGroupsModal && (
                <PortfolioGroupsModal
                    portfolios={[...pending, ...ready]}
                    products={tags}
                    groups={groups}
                    onSave={handleSaveGroups}
                    onClose={() => setShowGroupsModal(false)}
                />
            )}
            {/* ── Portfolios title + Search inline ── */}
            <div className="ps-title-row">
                <span className="ps-title">Portfolios</span>
                <Select
                    options={sortedPortfolioOptions}
                    className="search-select-field external-portfolio-selector"
                    classNamePrefix="search-select-field"
                    placeholder="Search"
                    value={null}
                    components={{ IndicatorSeparator: () => null, DropdownIndicator }}
                    blurInputOnSelect={true}
                    onChange={(fund) => chooseFund(fund.value)}
                    menuPosition="fixed"
                    styles={{
                        menuList: (provided) => ({ ...provided, maxHeight: "max(180px, calc(98vh - 472px))", overflowY: "auto" }),
                        option: (provided) => ({ ...provided, padding: "0.75rem 0.5rem" })
                    }}
                />
            </div>
            {/* ── Context chip + filter tags (only when no groups) ── */}
            {!hasGroups && (
                <div className="ps-chip-row">
                    {tags.filter(t => !t.disabled).map((p, index) => (
                        <div key={p.offerName} className={`product-tag${p.selected ? " selected" : ""}${p.disabled ? " disabled" : ""}`} onClick={() => p.disabled ? null : updateTags(tags.indexOf(p))}>{p.offerName}</div>
                    ))}
                </div>
            )}
            {/* ── Pending SubsectionHeader ── */}
            <div className="ssh">
                <button className="ssh-toggle" onClick={() => setPendingExpanded(v => !v)}>
                    <ChevronDown size={14} className={`ssh-caret${pendingExpanded ? '' : ' ssh-caret--collapsed'}`} />
                    <span className="ssh-title">Pending</span>
                    <span className="ssh-chip">
                        <span>{pending.length}</span><span className="ssh-chip-max">/{portfolios?.length}</span>
                    </span>
                </button>
                <Tooltip label="Manage Groups" placement="top" delay={400}>
                    <button className="ssh-action" onClick={() => setShowGroupsModal(true)}>
                        <FolderPlus size={14} />
                    </button>
                </Tooltip>
            </div>
            <div className='porfolios-list-wrapper' style={{ overflowY: "auto", maxHeight: height - 510 }}>
                {pendingExpanded && <>
                    {/* ── Groups ── */}
                    {pendingGroups.some(g => pending.filter(p => g.fundNumbers.includes(p.fundNumber)).filter(p => tagIsSelected(p)).length > 0) && (
                        <div className="sg-sublabel">
                            <span>GROUPS</span>
                            <span className="sg-sublabel-count">{pendingGroups.filter(g => pending.filter(p => g.fundNumbers.includes(p.fundNumber)).filter(p => tagIsSelected(p)).length > 0).length}</span>
                            <span className="sg-sublabel-line" aria-hidden="true" />
                        </div>
                    )}
                    {pendingGroups.map(group => {
                        const members = pending.filter(p => group.fundNumbers.includes(p.fundNumber)).filter(p => tagIsSelected(p));
                        if (members.length === 0) return null;
                        const isGroupSelected = selectedGroup?.id === group.id;
                        return (
                            <PortfolioGroup
                                key={group.id}
                                group={group}
                                members={members}
                                status="pending"
                                videoModes={videoModes}
                                showVideoToggle={showVideoToggle}
                                isSelected={isGroupSelected}
                                isExpanded={isGroupExpanded(group.id)}
                                selectedFundNumber={selectedFund?.fundNumber}
                                recentlyMoved={recentlyMovedGroup}
                                renamingGroupId={renamingGroupId}
                                renameValue={renameValue}
                                isKebabOpen={openKebab === group.id}
                                onHeaderClick={() => { if (renamingGroupId !== group.id) { selectGroup(group, 'pending'); toggleGroupExpand(group.id); } }}
                                onMemberClick={(p) => { setSelectedFund(p); setSelectedGroup(group); }}
                                onMemberToggleMode={(p, e) => togglePortfolioMode(p, group, e)}
                                onGroupToggleMode={(mems, e) => toggleGroupMode(group, mems, 'pending', e)}
                                onMoveGroup={() => moveGroupToReady(group)}
                                onCommitRename={() => commitRename(group.id)}
                                onCancelRename={() => { setRenamingGroupId(null); setRenameValue(''); }}
                                onRenameChange={setRenameValue}
                                onKebabClick={e => {
                                    e.stopPropagation();
                                    const isKebabOpen = openKebab === group.id;
                                    if (!isKebabOpen) {
                                        const r = e.currentTarget.getBoundingClientRect();
                                        setKebabPos({ top: r.bottom + 4, left: Math.max(8, r.right - 260) });
                                    }
                                    setOpenKebab(isKebabOpen ? null : group.id);
                                }}
                            />
                        );
                    })}

                    {/* ── Ungrouped portfolios ── */}
                    {(() => {
                        const open = isUngroupedExpanded('pending');
                        const members = ungroupedPending.filter(p => tagIsSelected(p));
                        if (members.length === 0) return null;
                        return (
                            <div className="sg-group sg-group--ungrouped">
                                <div className="sg-header" onClick={() => toggleUngroupedExpand('pending')}>
                                    <div className="sg-name-group">
                                        <span className="sg-name">Ungrouped</span>
                                        <span className="sg-pill">{members.length}</span>
                                    </div>
                                </div>
                                {open && (
                                    <ul className="sg-body">
                                        {members.map(p => {
                                            const isSelected = !selectedGroup && selectedFund?.fundNumber === p.fundNumber;
                                            const isVideo = videoModes[p.fundNumber] === 'video';
                                            return (
                                                <li key={p.fundNumber} className={`sg-row${isSelected ? ' sg-row--selected' : ''}`}
                                                    onClick={() => { setSelectedFund(p); setSelectedGroup(null); }}>
                                                    <Tooltip label={p.fundName} placement="right" delay={1200} outerStyle={{ flex: 1, minWidth: 0 }}>
                                                        <span className="sg-row-name">{p.fundName}</span>
                                                    </Tooltip>
                                                    {showVideoToggle && (
                                                        <Tooltip label={isVideo ? 'Switch to Editor' : 'Switch to Video'} placement="top" delay={600}>
                                                            <button
                                                                className={`sg-mode-btn${isVideo ? ' sg-mode-btn--video' : ''}`}
                                                                onClick={e => handleSetContentType(p, isVideo ? 'editor' : 'video', e)}>
                                                                {isVideo ? <Video size={12} style={{ color: '#1FA3FF' }} /> : <FileText size={12} style={{ color: '#1FA3FF' }} />}
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip label="Move to Ready to Publish" placement="top" delay={600}>
                                                        <button className="sg-mode-btn" onClick={e => { e.stopPropagation(); moveSingleToReady(p); }} title="Move to Ready to Publish">
                                                            <CheckCircle size={12} style={{ color: '#C2C4CE' }} />
                                                        </button>
                                                    </Tooltip>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        );
                    })()}
                </>}
            </div>
            {/* ── Ready to Publish SubsectionHeader ── */}
            <div className="ssh ssh--ready">
                <button className="ssh-toggle" onClick={() => setReadyExpanded(v => !v)}>
                    <ChevronDown size={14} className={`ssh-caret${readyExpanded ? '' : ' ssh-caret--collapsed'}`} />
                    <span className="ssh-title">Ready to Publish</span>
                    <span className="ssh-chip">
                        <span>{ready.length}</span><span className="ssh-chip-max">/{portfolios?.length}</span>
                    </span>
                </button>
            </div>
            <div className='porfolios-list-wrapper'>
                {readyExpanded && <>
                    {/* ── Ready Groups ── */}
                    {readyGroups.some(g => ready.filter(p => g.fundNumbers.includes(p.fundNumber)).filter(p => tagIsSelected(p)).length > 0) && (
                        <div className="sg-sublabel">
                            <span>GROUPS</span>
                            <span className="sg-sublabel-count">{readyGroups.filter(g => ready.filter(p => g.fundNumbers.includes(p.fundNumber)).filter(p => tagIsSelected(p)).length > 0).length}</span>
                            <span className="sg-sublabel-line" aria-hidden="true" />
                        </div>
                    )}
                    {readyGroups.map(group => {
                        const members = ready.filter(p => group.fundNumbers.includes(p.fundNumber)).filter(p => tagIsSelected(p));
                        if (members.length === 0) return null;
                        const isGroupSelected = selectedGroup?.id === group.id;
                        return (
                            <PortfolioGroup
                                key={group.id}
                                group={group}
                                members={members}
                                status="ready"
                                videoModes={videoModes}
                                showVideoToggle={showVideoToggle}
                                isSelected={isGroupSelected}
                                isExpanded={isGroupExpanded(group.id)}
                                selectedFundNumber={selectedFund?.fundNumber}
                                onHeaderClick={() => { selectGroup(group, 'ready'); toggleGroupExpand(group.id); }}
                                onMemberClick={(p) => { setSelectedFund(p); setSelectedGroup(group); selectedGroupStatusRef.current = 'ready'; }}
                                onMemberToggleMode={(p, e) => togglePortfolioMode(p, group, e)}
                                onMoveGroup={() => moveGroupToPending(group)}
                            />
                        );
                    })}
                    {/* ── Ungrouped Ready portfolios ── */}
                    {(() => {
                        const open = isUngroupedExpanded('ready');
                        const members = ungroupedReady.filter(p => tagIsSelected(p));
                        if (members.length === 0) return null;
                        return (
                            <div className="sg-group sg-group--ungrouped">
                                <div className="sg-header" onClick={() => toggleUngroupedExpand('ready')}>
                                    <div className="sg-name-group">
                                        <span className="sg-name">Ungrouped</span>
                                        <span className="sg-pill">{members.length}</span>
                                    </div>
                                </div>
                                {open && (
                                    <ul className="sg-body">
                                        {members.map(p => {
                                            const isSelected = !selectedGroup && selectedFund?.fundNumber === p.fundNumber;
                                            const isVideo = videoModes[p.fundNumber] === 'video';
                                            return (
                                                <li key={p.fundNumber} className={`sg-row${isSelected ? ' sg-row--selected' : ''}`}
                                                    onClick={() => { setSelectedFund(p); setSelectedGroup(null); }}>
                                                    <Tooltip label={p.fundName} placement="right" delay={1200} outerStyle={{ flex: 1, minWidth: 0 }}>
                                                        <span className="sg-row-name">{p.fundName}</span>
                                                    </Tooltip>
                                                    {showVideoToggle && (
                                                        <Tooltip label={isVideo ? 'Switch to Editor' : 'Switch to Video'} placement="top" delay={600}>
                                                            <button
                                                                className="sg-mode-btn"
                                                                disabled
                                                                style={{ opacity: 0.3, cursor: 'default', pointerEvents: 'none' }}>
                                                                {isVideo ? <Video size={12} style={{ color: '#1FA3FF' }} /> : <FileText size={12} style={{ color: '#1FA3FF' }} />}
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip label="Move back to Pending" placement="top" delay={600}>
                                                        <button className="sg-mode-btn" onClick={e => { e.stopPropagation(); moveToPending(p); }}>
                                                            <CornerLeftUp size={12} />
                                                        </button>
                                                    </Tooltip>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        );
                    })()}
                </>}
            </div>
            <div className="sidebar-section-divider" />
            <div>
                <Button fullWidth hover="default" variant="positive" disabled={pending.length > 0 || (pending.length === 0 && ready.length === 0) || completed} className="mt-m" text="Publish without review" onClick={() => markAsComplete(true)}>{completed ? "Completed" : "Complete"}</Button>
            </div>
            <div className='mt-1'>
                {completed && completedDetails && <div>
                    <AlertBlock
                        justify="center"
                        className="mt-m"
                        type={"positive"}
                        small
                        fullWidth
                    >
                        Commentaries submitted as complete by {completedDetails.user} on {formatDate(completedDetails.date)}
                    </AlertBlock>
                </div>}
            </div>
        </div>
        {openKebab && kebabPos && (() => {
            const group = (groups || []).find(g => g.id === openKebab);
            return (
                <GroupKebabPopover
                    group={group}
                    kebabPos={kebabPos}
                    confirmDeleteId={confirmDeleteId}
                    setConfirmDeleteId={setConfirmDeleteId}
                    confirmModeChange={confirmModeChange}
                    setConfirmModeChange={setConfirmModeChange}
                    onMoveToReady={() => { setOpenKebab(null); moveGroupToReady(group); }}
                    onRename={() => { setOpenKebab(null); setRenameValue(group.name); setRenamingGroupId(group.id); }}
                    onDeleteConfirmed={() => { setConfirmDeleteId(null); setOpenKebab(null); deleteGroupById(group.id); }}
                    onToggleLinkedConfirmed={(newLinked) => { toggleGroupLinked(group.id, newLinked); setConfirmModeChange(null); setOpenKebab(null); }}
                    onClose={() => { setOpenKebab(null); setConfirmDeleteId(null); setConfirmModeChange(null); }}
                />
            );
        })()}
        {readyPrecheck && (
            <Modal
                header={readyPrecheck.portfolio ? 'This portfolio is missing content' : 'Some portfolios are missing content'}
                handleClose={() => setReadyPrecheck(null)}
                cancelButton={{ label: 'Cancel', onClick: () => setReadyPrecheck(null) }}
                confirmButton={{
                    label: 'Move to Ready anyway',
                    variant: 'warning',
                    onClick: () => {
                        const { group: g, portfolio: pf } = readyPrecheck;
                        setReadyPrecheck(null);
                        if (pf) moveToReady(pf);
                        else doMoveGroupToReady(g);
                    },
                }}
            >
                {readyPrecheck.portfolio ? (
                    <p><strong>{readyPrecheck.portfolio.fundName}</strong> is missing required content. If you continue, it will still be marked as <strong>Ready to Publish</strong>.</p>
                ) : (
                    <p>The following portfolios in <strong>{readyPrecheck.group.name}</strong> are missing required content. If you continue, they will still be marked as <strong>Ready to Publish</strong>.</p>
                )}
                <ul className="mt-m">
                    {readyPrecheck.incomplete.map(m => (
                        <li key={m.fundNumber}>{m.fundName} — {m.reason}</li>
                    ))}
                </ul>
            </Modal>
        )}
        </>
    )
}

export default ExternalManagerPortfoliosSelector;